import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, CircularProgress, Alert } from '@mui/material';
import { Favorite as FavoriteIcon } from '@mui/icons-material';
import { eventApi } from '../utils/api';
import { Event } from '../types/event';
import EventCard from '../components/EventCard';
import { useNavigate } from 'react-router-dom';

const LikedEvents: React.FC = () => {
    const [likedEvents, setLikedEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadLikedEvents();
    }, []);

    const loadLikedEvents = async () => {
        try {
            setLoading(true);
            const events = await eventApi.getLikedEvents();
            setLikedEvents(events);
        } catch (err) {
            setError('Failed to load liked events');
        } finally {
            setLoading(false);
        }
    };

    const handleLikeToggle = async (eventId: string, isCurrentlyLiked: boolean) => {
        try {
            if (isCurrentlyLiked) {
                await eventApi.unlikeEvent(eventId);
                // Remove from list
                setLikedEvents(prev => prev.filter(e => e.id !== eventId));
            }
        } catch (err) {
            setError('Failed to update like status');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <FavoriteIcon sx={{ color: '#ff4081', fontSize: 32 }} />
                <Typography variant="h4" fontWeight="bold">
                    Liked Events
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {likedEvents.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <FavoriteIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No liked events yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Start liking events to see them here!
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {likedEvents.map(event => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={event.id}>
                            <EventCard
                                event={event}
                                onClick={() => navigate(`/event/${event.id}`)}
                                onLikeToggle={handleLikeToggle}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default LikedEvents;
